import type { RouteTemplateInput } from "@kitchen/schemas";
import { Button, Chip, faDigits, Field, IconButton, Panel, Seg, Switch } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, distributionApi, type RouteRow, type StaffRow } from "../../api.js";
import { askReason, ErrorLine, useAction } from "../common.js";
import { useMeals, useOrgs } from "../menu/data.js";

export function RoutesTab() {
  const meals = useMeals();
  const orgs = useOrgs();
  const staff = useQuery({ queryKey: ["staff"], queryFn: () => api<StaffRow[]>("/staff") });

  const liveMeals = (meals.data ?? []).filter((m) => m.active);
  const [selectedMealId, setSelectedMealId] = useState("");
  const activeMealId = selectedMealId || liveMeals[0]?.id || "";

  const routes = useQuery({
    queryKey: ["distribution", "routes", activeMealId],
    queryFn: () => distributionApi.listRoutes(activeMealId || undefined),
    enabled: Boolean(activeMealId),
  });

  const [editingRoute, setEditingRoute] = useState<RouteRow | "new" | null>(null);

  const archive = useAction(
    async ({ id, version, reason }: { id: string; version: number; reason: string }) => {
      await distributionApi.archiveRoute(id, version, reason);
    },
    [["distribution", "routes"]],
  );

  const staffMap = new Map((staff.data ?? []).map((s) => [s.id, s.displayName]));

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <div className="row wrap" style={{ gap: "var(--s3)", alignItems: "center" }}>
        {liveMeals.length > 0 && (
          <Seg
            label="وعده غذایی"
            value={activeMealId}
            onChange={(val) => {
              setSelectedMealId(val);
              setEditingRoute(null);
            }}
            options={liveMeals.map((m) => ({ value: m.id, label: m.name }))}
          />
        )}
        <span className="grow" />
        <Button
          variant="primary"
          icon="plus"
          onClick={() => {
            setEditingRoute("new");
          }}
        >
          قالب مسیر جدید
        </Button>
      </div>

      {editingRoute && (
        <RouteEditor
          route={editingRoute === "new" ? null : editingRoute}
          defaultMealId={activeMealId}
          meals={liveMeals}
          staff={staff.data ?? []}
          units={orgs.units}
          onClose={() => {
            setEditingRoute(null);
          }}
        />
      )}

      <Panel title="قالب‌های مسیر">
        {routes.isLoading && <p className="muted">در حال بارگذاری...</p>}
        {routes.data && routes.data.length === 0 && (
          <p className="ink2">هنوز قالب مسیری برای این وعده تعریف نشده است.</p>
        )}
        {routes.data && routes.data.length > 0 && (
          <div className="card" style={{ padding: "var(--s2) var(--s4)" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>نام مسیر</th>
                  <th>راننده پیش‌فرض</th>
                  <th>خودرو / مشخصات</th>
                  <th>تعداد توقف‌ها</th>
                  <th>وضعیت</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {routes.data.map((r) => (
                  <tr key={r.id}>
                    <td className="m b">{r.name}</td>
                    <td>{r.driverId ? (staffMap.get(r.driverId) ?? "—") : "تعیین‌نشده"}</td>
                    <td className="muted">{r.vehicleNote || "—"}</td>
                    <td className="num">{faDigits(r.stops.length)} ایستگاه</td>
                    <td>{r.active ? <Chip tone="ok">فعال</Chip> : <Chip plain>غیرفعال</Chip>}</td>
                    <td className="end">
                      <div className="row" style={{ gap: "var(--s2)", justifyContent: "flex-end" }}>
                        <Button
                          variant="ghost"
                          icon="edit"
                          onClick={() => {
                            setEditingRoute(r);
                          }}
                        >
                          ویرایش
                        </Button>
                        <Button
                          variant="ghost"
                          icon="x"
                          onClick={() => {
                            const reason = askReason(r.name);
                            if (reason) archive.run({ id: r.id, version: r.version, reason });
                          }}
                          disabled={archive.busy}
                        >
                          بایگانی
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <ErrorLine error={archive.error} />
      </Panel>
    </div>
  );
}

function RouteEditor({
  route,
  defaultMealId,
  meals,
  staff,
  units,
  onClose,
}: {
  route: RouteRow | null;
  defaultMealId: string;
  meals: { id: string; name: string }[];
  staff: StaffRow[];
  units: { id: string; name: string }[];
  onClose: () => void;
}) {
  const [name, setName] = useState(route?.name ?? "");
  const [mealId, setMealId] = useState(route?.mealId ?? defaultMealId);
  const [driverId, setDriverId] = useState(route?.driverId ?? staff[0]?.id ?? "");
  const [vehicleNote, setVehicleNote] = useState(route?.vehicleNote ?? "");
  const [active, setActive] = useState(route?.active ?? true);
  const [stops, setStops] = useState<{ unitId: string; sequence: number; notes?: string | undefined }[]>(
    route?.stops
      ? [...route.stops]
          .sort((a, b) => a.sequence - b.sequence)
          .map((s) => ({ unitId: s.unitId, sequence: s.sequence, notes: s.notes }))
      : [],
  );
  const [unitToAdd, setUnitToAdd] = useState("");

  useEffect(() => {
    if (!driverId && staff[0]) {
      setDriverId(staff[0].id);
    }
  }, [driverId, staff]);

  const save = useAction(async () => {
    const payload: RouteTemplateInput = {
      name: name.trim(),
      mealId,
      driverId,
      vehicleNote: vehicleNote.trim() ? vehicleNote.trim() : undefined,
      active,
      stops: stops.map((s, idx) => ({
        unitId: s.unitId,
        sequence: idx + 1,
        ...(s.notes ? { notes: s.notes } : {}),
      })),
    };

    if (!route) {
      await distributionApi.createRoute(payload);
    } else {
      await distributionApi.updateRoute(route.id, route.version, payload);
    }
    onClose();
  }, [["distribution", "routes"]]);

  const unitMap = new Map(units.map((u) => [u.id, u.name]));
  const existingUnitIds = new Set(stops.map((s) => s.unitId));
  const availableUnits = units.filter((u) => !existingUnitIds.has(u.id));

  const moveUp = (index: number) => {
    if (index <= 0) return;
    const next = [...stops];
    const prev = next[index - 1];
    const curr = next[index];
    if (prev && curr) {
      next[index - 1] = curr;
      next[index] = prev;
      setStops(next);
    }
  };

  const moveDown = (index: number) => {
    if (index >= stops.length - 1) return;
    const next = [...stops];
    const curr = next[index];
    const nextItem = next[index + 1];
    if (curr && nextItem) {
      next[index + 1] = curr;
      next[index] = nextItem;
      setStops(next);
    }
  };

  const removeStop = (index: number) => {
    setStops(stops.filter((_, i) => i !== index));
  };

  const handleAddStop = () => {
    if (!unitToAdd) return;
    setStops([...stops, { unitId: unitToAdd, sequence: stops.length + 1 }]);
    setUnitToAdd("");
  };

  return (
    <Panel title={route ? `ویرایش مسیر «${route.name}»` : "قالب مسیر جدید"}>
      <form
        className="stack"
        style={{ gap: "var(--s4)" }}
        onSubmit={(e) => {
          e.preventDefault();
          save.run(undefined);
        }}
      >
        <div
          className="grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "var(--s3)" }}
        >
          <Field
            id="route-name"
            label="نام مسیر"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            placeholder="مثلاً: مسیر ۱ - ولیعصر و ونک"
            required
          />

          <div className="field">
            <label htmlFor="route-meal">وعده غذایی</label>
            <select
              id="route-meal"
              className="input"
              value={mealId}
              onChange={(e) => {
                setMealId(e.target.value);
              }}
              required
            >
              {meals.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="route-driver">راننده پیش‌فرض</label>
            <select
              id="route-driver"
              className="input"
              value={driverId}
              onChange={(e) => {
                setDriverId(e.target.value);
              }}
              required
            >
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.displayName}
                </option>
              ))}
            </select>
          </div>

          <Field
            id="route-vehicle"
            label="مشخصات خودرو / یادداشت"
            value={vehicleNote}
            onChange={(e) => {
              setVehicleNote(e.target.value);
            }}
            placeholder="مثلاً: وانت آریسان - شماره پلاک..."
          />
        </div>

        <Switch label="این مسیر فعال است" checked={active} onChange={setActive} />

        <div className="stack" style={{ gap: "var(--s2)" }}>
          <h3 className="t-title">ایستگاه‌ها و ترتیب توزیع</h3>
          <div className="card" style={{ padding: "var(--s3)" }}>
            {stops.length === 0 ? (
              <p className="muted">هنوز هیچ ایستگاهی به این مسیر اضافه نشده است.</p>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>ترتیب</th>
                    <th>نام واحد / سازمان</th>
                    <th style={{ width: 140 }}>جابجایی</th>
                    <th style={{ width: 60 }} />
                  </tr>
                </thead>
                <tbody>
                  {stops.map((s, idx) => (
                    <tr key={s.unitId}>
                      <td className="num b">{faDigits(idx + 1)}</td>
                      <td className="m">{unitMap.get(s.unitId) ?? s.unitId}</td>
                      <td>
                        <div className="row" style={{ gap: "var(--s1)" }}>
                          <IconButton
                            label="حرکت به بالا"
                            icon="up"
                            disabled={idx === 0}
                            onClick={() => {
                              moveUp(idx);
                            }}
                          />
                          <IconButton
                            label="حرکت به پایین"
                            icon="down"
                            disabled={idx === stops.length - 1}
                            onClick={() => {
                              moveDown(idx);
                            }}
                          />
                        </div>
                      </td>
                      <td className="end">
                        <IconButton
                          label="حذف ایستگاه"
                          icon="x"
                          onClick={() => {
                            removeStop(idx);
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="row wrap" style={{ gap: "var(--s2)", alignItems: "end", marginTop: "var(--s2)" }}>
            <div className="field" style={{ minWidth: 260 }}>
              <label htmlFor="add-stop-select">افزودن واحد به مسیر</label>
              <select
                id="add-stop-select"
                className="input"
                value={unitToAdd}
                onChange={(e) => {
                  setUnitToAdd(e.target.value);
                }}
              >
                <option value="">انتخاب واحد...</option>
                {availableUnits.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <Button variant="default" icon="plus" onClick={handleAddStop} disabled={!unitToAdd}>
              افزودن ایستگاه
            </Button>
          </div>
        </div>

        <ErrorLine error={save.error} />

        <div className="row" style={{ gap: "var(--s2)", justifyContent: "flex-end", marginTop: "var(--s3)" }}>
          <Button variant="ghost" onClick={onClose} disabled={save.busy}>
            انصراف
          </Button>
          <Button variant="primary" type="submit" disabled={save.busy || !name.trim()}>
            {save.busy ? "در حال ثبت..." : "ذخیره قالب مسیر"}
          </Button>
        </div>
      </form>
    </Panel>
  );
}
